import React, { HTMLAttributes, useEffect, useRef, useState } from "react";
import { AiOutlineLoading } from "react-icons/ai";

// import required modules
import { twMerge } from "tailwind-merge";
import {
  PARKING_NAME_EN_MAP,
  REGION_DISTRICT_MAP,
  REGION_LIST,
  STATION_INFO_MAP,
} from ".";
import { getEVInfo } from "../../../apis/parkingbeesRelatedService";
import type { IParkingBeesEvChargerVacancyResponse } from "../../../apis/parkingbeesRelatedService/type";

interface IEvInfoSwiperDesktop {
  goNextSection: () => void;
}

type TEvInfoSwiperDesktopProps = IEvInfoSwiperDesktop &
  HTMLAttributes<HTMLDivElement>;

const EvInfoSwiperDesktop: React.FC<TEvInfoSwiperDesktopProps> = (props) => {
  const { goNextSection, ...otherProps } = props;
  const [selectedRegion, setSelectedRegion] = useState<string>("all");
  const [isInited, setIsInited] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});

  const [evInfoList, setEvInfoList] = useState<
    IParkingBeesEvChargerVacancyResponse[]
  >([]);
  const desktopListRef = useRef<HTMLDivElement>(null);

  function filterByRegion(
    list: IParkingBeesEvChargerVacancyResponse[],
    regionKey: string,
  ) {
    if (!Array.isArray(list)) return [];
    if (regionKey === "all") return list;
    const districtIds = REGION_DISTRICT_MAP[regionKey] || [];
    return list.filter((item) =>
      districtIds.includes(item.parking.district_id),
    );
  }

  const fetchAllEVInfo = async () => {
    try {
      const evInfoResponse = await getEVInfo();

      setEvInfoList(evInfoResponse);
    } catch (error) {
      console.error(error);
    } finally {
      setIsInited(true);
    }
  };

  useEffect(() => {
    fetchAllEVInfo();
    const intervalId = setInterval(
      () => {
        fetchAllEVInfo();
      },
      60 * 1000 * 3,
    ); // Update every 3 minutes

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, []);

  useEffect(() => {
    const prevInfo = filteredEvInfoList[selectedIndex] || {};
    const prevAvailable = prevInfo?.availableCount;
    const prevEstimate = prevInfo?.estimatedWaitingTime;

    setLoadingMap((prev) => ({
      ...prev,
      [selectedIndex]: true,
    }));

    fetchAllEVInfo().then(() => {
      const newInfo =
        filterByRegion(evInfoList, selectedRegion)[selectedIndex] || {};
      const newAvailable = newInfo?.availableCount;
      const newEstimate = newInfo?.estimatedWaitingTime;

      // 只有有變化才顯示 loading
      if (prevAvailable !== newAvailable || prevEstimate !== newEstimate) {
        setLoadingMap((prev) => ({
          ...prev,
          [selectedIndex]: true,
        }));
        setTimeout(() => {
          setLoadingMap((prev) => ({
            ...prev,
            [selectedIndex]: false,
          }));
        }, 800); // loading動畫持續時間，可調整
      } else {
        setLoadingMap((prev) => ({
          ...prev,
          [selectedIndex]: false,
        }));
      }
    });
    // eslint-disable-next-line
  }, [selectedIndex]);

  const filteredEvInfoList = Array.isArray(evInfoList)
    ? filterByRegion(evInfoList, selectedRegion)
    : [];

  return (
    <div className="hidden laptop:flex w-[85vw]">
      {/* info section */}
      <div
        className={twMerge(
          "flex flex-col w-full gap-10 mx-0 p-5 rounded-3xl bg-mainDark text-white transition-all duration-500",
        )}
      >
        {/* filter */}
        <div className="flex flex-row justify-center gap-12 text-center">
          {REGION_LIST.map((region) => (
            <div
              key={region.key}
              className={twMerge(
                "flex flex-col",
                selectedRegion === region.key
                  ? "text-white font-bold border-b-2 border-white pb-2"
                  : "text-gray-400 cursor-pointer",
              )}
              onClick={() => {
                setSelectedRegion(region.key);
                setSelectedIndex(0);
                // scroll to top of the list
                desktopListRef.current?.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }}
            >
              <span className="font-bold text-xl">{region.name}</span>
              <span className="text-base">{region.eng_name}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-row gap-4">
          <div
            className="flex flex-[2] flex-col gap-4 max-h-[400px] overflow-auto"
            ref={desktopListRef}
            onMouseEnter={() =>
              window.dispatchEvent(new CustomEvent("swiper-mousewheel-disable"))
            }
            onMouseLeave={() =>
              window.dispatchEvent(new CustomEvent("swiper-mousewheel-enable"))
            }
            onTouchStart={() =>
              window.dispatchEvent(new CustomEvent("swiper-mousewheel-disable"))
            }
            onTouchEnd={() =>
              window.dispatchEvent(new CustomEvent("swiper-mousewheel-enable"))
            }
            onFocus={() =>
              window.dispatchEvent(new CustomEvent("swiper-mousewheel-disable"))
            }
            onBlur={() =>
              window.dispatchEvent(new CustomEvent("swiper-mousewheel-enable"))
            }
            tabIndex={0}
          >
            {!isInited &&
              Array.from({ length: 3 }).map((_, idx) => (
                <div
                  key={idx}
                  className="flex flex-row gap-4 mx-0 p-5 rounded-xl bg-[#444] animate-pulse w-full min-h-[220px]"
                >
                  <div className="flex flex-1 flex-col gap-4">
                    <div className="h-8 bg-[#666] rounded-xl mb-2" />
                    <div className="w-[92px] h-[92px] rounded-full bg-[#666]" />
                    <div className="flex flex-row gap-2">
                      <div className="w-8 h-8 bg-[#666] rounded" />
                      <div className="w-8 h-8 bg-[#666] rounded" />
                    </div>
                  </div>
                  <div className="flex flex-[3] flex-col gap-5">
                    <div className="h-6 bg-[#666] rounded mb-2" />
                    <div className="h-10 bg-[#666] rounded mb-2" />
                    <div className="h-6 bg-[#666] rounded mb-2" />
                  </div>
                </div>
              ))}
            {isInited &&
              Array.isArray(filteredEvInfoList) &&
              filteredEvInfoList.map(
                (
                  evInfo: IParkingBeesEvChargerVacancyResponse,
                  index: number,
                ) => {
                  return (
                    <div
                      key={evInfo.ms_token}
                      className={twMerge(
                        "flex flex-row gap-4 mx-0 p-5 rounded-xl bg-[#666666] text-white w-full min-h-[220px] cursor-pointer",
                        selectedIndex === index ? "opacity-100" : "opacity-20",
                      )}
                      onClick={() => setSelectedIndex(index)}
                    >
                      {/* left */}
                      <div className="flex flex-[1.4] flex-row gap-4 border-r-2 border-secondaryDark p-4">
                        <div className="flex flex-col gap-4 items-center">
                          <div className="w-[120px] font-bold text-sm rounded-xl bg-secondaryDark text-center p-2">
                            {evInfo.parking.name}
                            <br />
                            {PARKING_NAME_EN_MAP[evInfo.parking.name]}
                          </div>
                          <img
                            src={
                              evInfo.parking.id === 2
                                ? "https://parkingbees-global-images.s3.ap-southeast-1.amazonaws.com/khora_ev_cover.jpeg"
                                : evInfo.parking.cover
                            }
                            alt="address-image"
                            className="flex w-[100px] h-[100px] aspect-square rounded-full object-contain bg-black"
                          />
                        </div>
                        <div className="flex flex-col justify-between">
                          <div className="flex flex-col gap-2">
                            <div className="text-xl">
                              {evInfo.parking.display_name}
                            </div>
                            <div>
                              {STATION_INFO_MAP[evInfo.parking.id]?.name_eng}
                            </div>
                          </div>

                          <div className="flex flex-row items-center gap-8">
                            <img
                              src={require("../../../assets/icons/free_parking_1hr_desktop.png")}
                              width={100}
                              height={70}
                              alt="free_parking_1hr"
                            />

                            <a
                              href={evInfo.parking.map}
                              target="_blank"
                              referrerPolicy="no-referrer"
                              rel="noreferrer"
                            >
                              <img
                                src={require("../../../assets/icons/locationArrow.png")}
                                width={30}
                                height={70}
                                alt="location"
                              />
                            </a>
                          </div>
                        </div>
                      </div>
                      {/* right */}
                      <div className="flex flex-1 flex-col justify-between gap-5 p-4">
                        {evInfo.totalCount === 0 ? (
                          <div className="text-4xl text-mainYellow text-center mt-12">
                            即將推出
                            <br />
                            Coming Soon
                          </div>
                        ) : (
                          <>
                            <div className="flex flex-row justify-between items-end">
                              <div className="flex-1 text-xl font-semibold">
                                閒置中
                                <br />
                                <span className="text-sm">Available</span>
                              </div>
                              <div className="flex flex-row items-end text-secondaryDark text-4xl font-bold">
                                <span className="text-mainLightBlue text-6xl">
                                  {loadingMap[index] ? (
                                    <AiOutlineLoading className="text-6xl animate-spin" />
                                  ) : (
                                    <>{evInfo.availableCount}</>
                                  )}
                                </span>
                                /<span>{evInfo.totalCount}</span>
                              </div>
                            </div>
                            <div className="flex flex-row justify-between items-center">
                              <div className="flex-1 text-xl font-semibold">
                                預計等待時間
                                <br />
                                <span className="text-sm">
                                  Estimated Waiting Time
                                </span>
                              </div>
                              <div className="text-mainLightBlue text-lg font-bold">
                                {loadingMap[index] ? (
                                  <AiOutlineLoading className="text-4xl animate-spin" />
                                ) : (
                                  <>~{evInfo.estimatedWaitingTime} 分鐘</>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                },
              )}

            {isInited && filteredEvInfoList.length === 0 && (
              <div className="flex flex-row rounded-xl justify-center items-center bg-[#666666] w-full h-[220px] text-center text-4xl ">
                即將推出
                <br />
                Coming Soon
              </div>
            )}
          </div>

          {/* price section */}
          {filteredEvInfoList.length > 0 && (
            <div className="flex-1">
              <div className="flex flex-col px-4 gap-8">
                <img
                  width={160}
                  className="px-2"
                  src={require("../../../assets/images/standard_fees.png")}
                  alt="Standard Fees"
                />

                <div className="flex px-2 flex-row justify-between">
                  <div className="font-semibold text-xl w-[80px] mt-5 text-secondaryDark">
                    充電價格
                    <br />
                    <span className="text-sm leading-3">Charging Fee</span>
                  </div>
                  <div className="flex flex-col gap-2 text-right font-semibold">
                    <span className="text-6xl text-mainYellow">
                      $
                      {filteredEvInfoList[selectedIndex]?.priceMin ??
                        STATION_INFO_MAP[
                          filteredEvInfoList[selectedIndex]?.parking.id
                        ]?.price}
                    </span>
                    <span className="text-4xl text-secondaryDark"> / kWh</span>
                    <span className="text-xl">
                      {STATION_INFO_MAP[
                        filteredEvInfoList[selectedIndex]?.parking.id
                      ]?.time_period ?? "-"}
                    </span>
                  </div>
                </div>

                <div className="flex px-2 relative">
                  <img
                    src={require("../../../assets/images/bubble_desktop.png")}
                    alt="bubble_d"
                    className="w-full h-auto"
                  />
                  <div className="absolute flex flex-row text-white left-[32%] top-[28px] text-center items-baseline">
                    <span className="text-base">Up to</span>
                    <span className="text-2xl font-semibold ml-2">
                      {STATION_INFO_MAP[
                        filteredEvInfoList[selectedIndex]?.parking.id
                      ]?.kwh ?? "-"}{" "}
                      kW
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EvInfoSwiperDesktop;
